// src/lomavisSharedUi/templates/sections/LomavisPaidAnalyticsView1/LomavisPaidAnalyticsView1.tsx
import React from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Stack } from 'react-bootstrap';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import {
  LomavisTabItem,
  LomavisTabSwitcherCard1
} from '@/lomavisSharedUi/atoms/tabs/LomavisTabSwitcherCard1';
import { LomavisRowSelect1 } from '@/lomavisSharedUi/atoms/table/LomavisRowSelect1';
import { LomavisShortenedNumber } from '@/lomavisSharedUi/atoms/utils/numbers/LomavisShortenedNumber';
import { LomavisOverlayToolTip1 } from '@/lomavisSharedUi/atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';
import './index.css';
import { PLATFORM_STATIC_ICONS_PNG } from '@/config/icons';
import { META } from '@/config/constants';
import LomavisMoreInfoIcon from '@/lomavisSharedUi/atoms/icons/LomavisMoreInfoIcon/LomavisMoreInfoIcon';

// ------------ Types ------------
export type InsightMap = Record<string, number | string | null | undefined>;

export type PaidAd = {
  id: string;
  name: string;
  preview_image_url?: string;
  date_start?: string | null;
  date_stop?: string | null;
  status?: string;
  insights?: InsightMap;
};

export type PaidAdset = {
  id: string;
  name: string;
  preview_image_url?: string;
  status?: string;
  insights?: InsightMap;
  ads?: PaidAd[];
};

export type PaidCampaign = {
  id: string;
  name: string;
  status?: string;
  objective?: string;
  preview_image_url?: string;
  start_time?: string;
  stop_time?: string;
  insights?: InsightMap;
  adsets?: PaidAdset[];
};

export interface LomavisPaidAnalyticsView1Props {
  data: PaidCampaign[]; // normalized dataset
  accentColor?: string; // forwarded to tab switcher
  metricUnits?: Record<string, string | undefined>; // optional units per metric key
}

// -------------------- Helpers --------------------
const statusToUi = (status?: string): 'Active' | 'Paused' | 'Cancel' => {
  const s = (status || '').toLowerCase();
  if (s === 'active') return 'Active';
  if (s === 'paused') return 'Paused';
  return 'Cancel';
};

const getMetric = (insights: InsightMap | undefined, key: string) => {
  const v = insights?.[key];
  return v ?? '-';
};

const renderTableValue = (
  value: number | string | null | undefined,
  unit?: string
) => {
  const hasValue = value !== null && value !== undefined && value !== '';
  const unitSuffix = unit ? ` ${unit}` : '';
  const tooltipText =
    typeof value === 'number' && Number.isFinite(value)
      ? `${value.toLocaleString()}${unitSuffix}`
      : hasValue
      ? `${String(value)}${unitSuffix}`
      : '-';

  if (typeof value === 'number' && Number.isFinite(value)) {
    return (
      <LomavisOverlayToolTip1 text={tooltipText}>
        <LomavisShortenedNumber value={value} dataUnit={unit} />
      </LomavisOverlayToolTip1>
    );
  }

  const displayText = hasValue ? `${String(value)}${unitSuffix}` : '-';

  return <span>{displayText}</span>;
};

const flattenAdsets = (
  campaigns: PaidCampaign[]
): Array<
  PaidAdset & {
    campaignId: string;
    campaignName: string;
    campaignStatus?: string;
  }
> => {
  const rows: Array<
    PaidAdset & {
      campaignId: string;
      campaignName: string;
      campaignStatus?: string;
    }
  > = [];
  for (const c of campaigns) {
    for (const as of c.adsets ?? []) {
      rows.push({
        ...as,
        campaignId: c.id,
        campaignName: c.name,
        campaignStatus: c.status
      });
    }
  }
  return rows;
};

const flattenAds = (
  adsets: PaidAdset[]
): Array<PaidAd & { adsetId: string; adsetName: string }> => {
  const rows: Array<PaidAd & { adsetId: string; adsetName: string }> = [];
  for (const as of adsets)
    for (const ad of as.ads ?? [])
      rows.push({ ...ad, adsetId: as.id, adsetName: as.name });
  return rows;
};

const collectInsightKeys = (rows: Array<{ insights?: InsightMap }>) => {
  const keys = new Set<string>();
  for (const r of rows)
    if (r?.insights) Object.keys(r.insights).forEach((k) => keys.add(k));
  return Array.from(keys);
};

const sortKeysWithPreference = (keys: string[], preferred: string[]) => {
  const set = new Set(keys);
  const ordered: string[] = [];
  for (const k of preferred) if (set.has(k)) ordered.push(k);
  const rest = keys
    .filter((k) => !preferred.includes(k))
    .sort((a, b) => a.localeCompare(b));
  return [...ordered, ...rest];
};

// Helper function to convert any value to a number for sorting
const toNumber = (v: any): number => {
  if (v === null || v === undefined || v === '' || v === '-') return 0;
  const n = typeof v === 'string' ? Number(v.replace(/[, ]/g, '')) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const makeMetricColumns = (
  keys: string[],
  t: any,
  metricUnits?: Record<string, string | undefined>
): Array<TableColumn<any>> =>
  keys.map((key) => {
    const unit = metricUnits?.[key];
    return {
      name: (
        <span className="d-flex align-items-center gap-2">
          <span>{t(`${key.toUpperCase()}`)}</span>
          <LomavisMoreInfoIcon
            text={t(`${key.toUpperCase()}_tooltip`)}
            size={4}
          />
        </span>
      ),

      selector: (row: any) => getMetric(row.insights as InsightMap, key),
      cell: (row: any) => renderTableValue(row?.insights?.[key], unit),
      sortable: true,
      sortFunction: (rowA: any, rowB: any) => {
        const valueA = toNumber(rowA?.insights?.[key]);
        const valueB = toNumber(rowB?.insights?.[key]);
        return valueA - valueB;
      },
      right: true,
      compact: true,
      minWidth: '140px',
      style: { whiteSpace: 'nowrap' }
    };
  });

// -------------------- Component --------------------
export const LomavisPaidAnalyticsView1: React.FC<
  LomavisPaidAnalyticsView1Props
> = ({ data, accentColor, metricUnits }) => {
  const t = translateComponent('LomavisPaidAnalyticsView1');
  const FIRST_COL_WIDTH = 360;

  // Derived bases
  const campaignRows = React.useMemo(() => data, [data]);
  const adsetRowsAll = React.useMemo(() => flattenAdsets(data), [data]);
  const adRowsAll = React.useMemo(
    () => flattenAds(adsetRowsAll),
    [adsetRowsAll]
  );

  // Tabs & selections
  const [activeTab, setActiveTab] = React.useState<string>('campaigns');
  const [selectedCampaignIds, setSelectedCampaignIds] = React.useState<
    Set<string>
  >(new Set());
  const [selectedAdsetIds, setSelectedAdsetIds] = React.useState<Set<string>>(
    new Set()
  );

  // Filtering
  const adsetRowsFiltered = React.useMemo(() => {
    if (selectedCampaignIds.size === 0) return adsetRowsAll;
    return adsetRowsAll.filter((r) => selectedCampaignIds.has(r.campaignId));
  }, [adsetRowsAll, selectedCampaignIds]);

  const adRowsFiltered = React.useMemo(() => {
    if (selectedAdsetIds.size === 0) {
      if (selectedCampaignIds.size > 0) {
        const allowedAdsetIds = new Set(
          adsetRowsAll
            .filter((a) => selectedCampaignIds.has(a.campaignId))
            .map((a) => a.id)
        );
        return adRowsAll.filter((ad) => allowedAdsetIds.has(ad.adsetId));
      }
      return adRowsAll;
    }
    return adRowsAll.filter((r) => selectedAdsetIds.has(r.adsetId));
  }, [adRowsAll, adsetRowsAll, selectedAdsetIds, selectedCampaignIds]);

  // Common preferred metrics order for all levels
  const preferredMetricKeys = [
    'impressions',
    'reach',
    'clicks',
    'spent',
    'conversions',
    'conversion_value',
    'ctr',
    'cpm',
    'cpc',
    'cpr',
    'roas'
  ];

  // Dynamic metric keys - using the same preferred order for all levels
  const campaignMetricKeys = React.useMemo(() => {
    const raw = collectInsightKeys(campaignRows);
    return sortKeysWithPreference(raw, preferredMetricKeys);
  }, [campaignRows]);

  const adsetMetricKeys = React.useMemo(() => {
    const raw = collectInsightKeys(adsetRowsAll);
    return sortKeysWithPreference(raw, preferredMetricKeys);
  }, [adsetRowsAll]);

  const adMetricKeys = React.useMemo(() => {
    const raw = collectInsightKeys(adRowsAll);
    return sortKeysWithPreference(raw, preferredMetricKeys);
  }, [adRowsAll]);

  // First column renderers
  const campaignFirstCol: TableColumn<PaidCampaign> = {
    name: t('campaigns'),
    cell: (row) => (
      <div
        className="d-flex align-items-center w-100 overflow-hidden"
        style={{ minWidth: 0 }} // <- critical for ellipsis in flex children
      >
        <div className="w-100 overflow-hidden" style={{ minWidth: 0 }}>
          <LomavisRowSelect1
            checked={selectedCampaignIds.has(row.id)}
            onChange={() =>
              setSelectedCampaignIds((prev) => {
                const n = new Set(prev);
                n.has(row.id) ? n.delete(row.id) : n.add(row.id);
                setSelectedAdsetIds((prevAS) => {
                  if (n.size === 0) return prevAS;
                  const allowed = new Set(
                    adsetRowsAll
                      .filter((a) => n.has(a.campaignId))
                      .map((a) => a.id)
                  );
                  return new Set([...prevAS].filter((x) => allowed.has(x)));
                });
                return n;
              })
            }
            imageSrc={
              row.preview_image_url || 'https://placehold.co/100x100?text=Ad'
            }
            title={row.name}
            subtitle={row.objective}
            status={statusToUi(row.status)}
            showStatus={false}
          />
        </div>
      </div>
    ),
    // clamp the cell itself
    width: `${FIRST_COL_WIDTH}px`,
    // don't let it grow; sticky col stays a fixed width
    grow: 0,
    wrap: false,
    sortable: false,
    style: {
      maxWidth: `${FIRST_COL_WIDTH}px`,
      overflow: 'hidden', // <- required for ellipsis
      textOverflow: 'ellipsis', // <- just in case title spills beyond its wrapper
      whiteSpace: 'nowrap'
    }
  };

  const adsetFirstCol: TableColumn<any> = {
    name: t('ad_groups'),
    cell: (row) => (
      <div
        className="d-flex align-items-center w-100 overflow-hidden"
        style={{ minWidth: 0 }}
      >
        <div className="w-100 overflow-hidden" style={{ minWidth: 0 }}>
          <LomavisRowSelect1
            checked={selectedAdsetIds.has(row.id)}
            onChange={() =>
              setSelectedAdsetIds((prev) => {
                const n = new Set(prev);
                n.has(row.id) ? n.delete(row.id) : n.add(row.id);
                return n;
              })
            }
            imageSrc={
              row.preview_image_url || 'https://placehold.co/100x100?text=Ad'
            }
            title={row.name}
          />
        </div>
      </div>
    ),
    width: `${FIRST_COL_WIDTH}px`,
    grow: 0,
    wrap: false,
    sortable: false,
    style: {
      maxWidth: `${FIRST_COL_WIDTH}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  };

  const adFirstCol: TableColumn<any> = {
    name: t('ads'),
    cell: (row) => (
      <div
        className="d-flex align-items-center w-100 overflow-hidden"
        style={{ minWidth: 0 }}
      >
        <div className="w-100 overflow-hidden" style={{ minWidth: 0 }}>
          <LomavisRowSelect1
            checked={false}
            imageSrc={
              row.preview_image_url || 'https://placehold.co/100x100?text=Ad'
            }
            title={row.name}
          />
        </div>
      </div>
    ),
    width: `${FIRST_COL_WIDTH}px`,
    grow: 0,
    wrap: false,
    sortable: false,
    style: {
      maxWidth: `${FIRST_COL_WIDTH}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  };

  // Columns per tab
  const campaignColumns: Array<TableColumn<any>> = React.useMemo(() => {
    const metricCols = makeMetricColumns(campaignMetricKeys, t, metricUnits);
    return [campaignFirstCol, ...metricCols];
  }, [campaignMetricKeys, t, metricUnits]);

  const adsetColumns: Array<TableColumn<any>> = React.useMemo(() => {
    const metricCols = makeMetricColumns(adsetMetricKeys, t, metricUnits);
    return [adsetFirstCol, ...metricCols];
  }, [adsetMetricKeys, t, metricUnits]);

  const adColumns: Array<TableColumn<any>> = React.useMemo(() => {
    const metricCols = makeMetricColumns(adMetricKeys, t, metricUnits);
    return [adFirstCol, ...metricCols];
  }, [adMetricKeys, t, metricUnits]);

  // Active rows & columns
  const rows =
    activeTab === 'campaigns'
      ? campaignRows
      : activeTab === 'adsets'
      ? adsetRowsFiltered
      : adRowsFiltered;

  const columns =
    activeTab === 'campaigns'
      ? campaignColumns
      : activeTab === 'adsets'
      ? adsetColumns
      : adColumns;

  // Tabs (counts)
  const TABS: LomavisTabItem[] = [
    { key: 'campaigns', label: 'Campaigns', count: campaignRows.length },
    { key: 'adsets', label: 'Ad Groups', count: adsetRowsFiltered.length },
    { key: 'ads', label: 'Ads', count: adRowsFiltered.length }
  ];

  // ---- Custom pagination (footer stays fixed) ----
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(20);
  const totalRows = rows.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / perPage));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * perPage;
  const end = start + perPage;
  const pageRows = rows.slice(start, end);

  React.useEffect(() => {
    // clamp page when data changes
    const pc = Math.max(1, Math.ceil(rows.length / perPage));
    if (page > pc) setPage(pc);
  }, [rows, perPage]);

  // RDT styles (no horizontal scroll here)
  const customStyles = {
    table: { style: { backgroundColor: '#fff' } },
    tableWrapper: { style: { overflowX: 'hidden' } },
    headRow: { style: { backgroundColor: '#fff', minHeight: '44px' } },
    headCells: {
      style: { fontWeight: 600, color: '#99A1B7', whiteSpace: 'nowrap' }
    },
    cells: {
      style: {
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        whiteSpace: 'nowrap',
        fontWeight: 600,
        color: 'var(--bs-gray-600)',
        fontSize: 'var(--bs-body-font-size)', // ≈ .fs-6 (defaults to 1rem)
        lineHeight: 'var(--bs-body-line-height)'
      }
    },
    rows: { style: { borderBottom: '1px solid #EEF2F7' } }
  };

  return (
    <Stack gap={3} className="w-100">
      {/* Tabs (do not scroll) */}
      <LomavisTabSwitcherCard1
        tabs={TABS}
        activeKey={activeTab}
        onChange={(k) => {
          setActiveTab(k);
          setPage(1);
        }}
        accentColor={accentColor}
      />

      {/* Table: ONLY this scroller moves horizontally */}
      <div className="border rounded bg-white">
        <div className="lomavis-horizontal-scroller">
          <DataTable
            columns={columns}
            data={pageRows}
            customStyles={customStyles}
            highlightOnHover
            dense
            responsive={false} // horizontal scroll handled by wrapper
            persistTableHead
            fixedHeader={false}
            pagination={false} // custom pagination below
            className="lomavis-paid-table"
            noDataComponent={
              <div className="w-100 text-center py-5 my-4 d-flex justify-content-center align-items-center gap-2">
                {' '}
                <span className="">{t('emptyState')}</span>{' '}
                <img
                  src={PLATFORM_STATIC_ICONS_PNG[META]}
                  className="h-25px"
                  style={{ marginRight: 2 }}
                />
              </div>
            }
          />
        </div>

        {/* Fixed footer (custom pagination) */}
        <div className="d-flex justify-content-between align-items-center p-2">
          <div className="text-muted small">
            {t('pagination.showing', 'Showing')} {totalRows === 0 ? 0 : start + 1}–{Math.min(end, totalRows)}{' '}
            {t('pagination.of', 'of')} {totalRows}
          </div>
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value, 10));
                setPage(1);
              }}
              style={{ width: 96 }}
            >
              {[10, 20, 30, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('pagination.prev', 'Prev')}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={safePage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                {t('pagination.next', 'Next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Stack>
  );
};
